import { useId, useState } from 'react';
import { ArrowRight, Play, Sparkles, Timer } from 'lucide-react';
import { Card, Emblema, Pastilla } from '@/components/Card';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { actualizarSettings } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import type { Modalidad, PersonaRutinas } from '@/db/types';
import { rutinasQueFaltan, type RutinaConCambios, type RutinaParaPlantilla } from '@/db/rutinas';
import { PERSONAS, PLANTILLAS_DE_RUTINAS, plantillaDe } from '@/datos/rutinas-precargadas';
import { formatearCorto, nombreDia } from '@/domain/dates';
import type { SiguienteDelCiclo } from '@/domain/sesiones';

/**
 * Rutinas precargadas en Gym (Req. 10.8): elegir de quién es el teléfono con
 * un toque, ofrecer sus rutinas para revisarlas y crearlas, proponer la
 * sesión que sigue en las que no tienen días fijos y ofrecer los descansos
 * confirmados para las que ya se crearon.
 */

/** Cómo se dicen los descansos confirmados de las personas de esas rutinas. */
export function textoDescansosConfirmados(cambios: readonly RutinaConCambios[]): string {
  const personas = PERSONAS.filter((p) => cambios.some((c) => c.persona === p));
  if (personas.length === 1 && personas[0] !== undefined) return `${PLANTILLAS_DE_RUTINAS[personas[0]].descansos.texto}.`;
  return personas.map((p) => `${PLANTILLAS_DE_RUTINAS[p].nombrePersona}: ${PLANTILLAS_DE_RUTINAS[p].descansos.texto}.`).join(' ');
}

/**
 * Descansos confirmados (26 oct 2026): si las rutinas precargadas que ya están
 * en el teléfono tienen otros, ofrece revisarlos y actualizarlos. Sin
 * cambios pendientes no muestra nada.
 */
export function TarjetaDescansos({ cambios }: { cambios: readonly RutinaConCambios[] }) {
  if (cambios.length === 0) return null;
  const n = cambios.reduce((suma, r) => suma + r.cambios.length, 0);
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <Emblema icono={Timer} />
        <Pastilla>Confirmados</Pastilla>
      </div>
      <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">Descansos nuevos.</h2>
      <p className="mb-4 text-[13px] leading-relaxed text-texto-suave">
        {textoDescansosConfirmados(cambios)} Cambian {n === 1 ? '1 descanso' : `${n} descansos`} de{' '}
        {cambios.length === 1 ? '1 rutina' : `${cambios.length} rutinas`}; las cargas, las series y tu historial no
        cambian.
      </p>
      <BotonEnlace to="/gym/descansos" icono={ArrowRight} variante="secundario">
        Revisar y actualizar
      </BotonEnlace>
    </Card>
  );
}

/** Los dos botones para elegir de quién es el teléfono. Solo guarda la elección. */
export function ElegirPersona({ etiquetadoPor }: { etiquetadoPor: string }) {
  const [error, setError] = useState<string | null>(null);
  async function elegir(persona: PersonaRutinas) {
    setError(null);
    try {
      await actualizarSettings({ rutinasDe: persona });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }
  return (
    <div className="space-y-3">
      <div role="group" aria-labelledby={etiquetadoPor} className="grid grid-cols-2 gap-2">
        {PERSONAS.map((p) => (
          <Button key={p} variante="secundario" onClick={() => void elegir(p)}>
            {PLANTILLAS_DE_RUTINAS[p].nombrePersona}
          </Button>
        ))}
      </div>
      {error !== null && (
        <Banner tono="alerta" titulo="No se guardó">
          {error}
        </Banner>
      )}
    </div>
  );
}

/**
 * En Gym, en la modalidad que se ve: sin elegir, pregunta de quién es el
 * teléfono; elegido, ofrece las rutinas que falten. Si ya están todas, o si
 * esa persona todavía no tiene rutina de casa, no muestra nada.
 */
export function TarjetaPrecargadas({
  persona,
  modalidad,
  rutinas,
}: {
  persona: PersonaRutinas | null;
  modalidad: Modalidad;
  rutinas: readonly RutinaParaPlantilla[];
}) {
  const idTitulo = useId();
  const plantilla = plantillaDe(persona, modalidad);

  if (persona !== null && plantilla === null) return null;
  if (plantilla === null) {
    return (
      <Card>
        <div className="flex items-start justify-between gap-3">
          <Emblema icono={Sparkles} />
          <Pastilla>Una sola vez</Pastilla>
        </div>
        <span className="antetitulo mt-3 block">Rutinas precargadas</span>
        <h2 id={idTitulo} className="mb-2 mt-2 text-[22px] leading-tight tracking-[-0.6px] text-texto">
          ¿De quién es este teléfono?
        </h2>
        <p className="mb-4 text-[13px] leading-relaxed text-texto-suave">
          Te ofrecemos las rutinas que nos diste, listas para revisarlas y crearlas. Elegir no cambia ni borra nada.
        </p>
        <ElegirPersona etiquetadoPor={idTitulo} />
      </Card>
    );
  }

  const faltan = rutinasQueFaltan(rutinas, plantilla.rutinas);
  if (faltan.length === 0) return null;
  const parcial = faltan.length < plantilla.rutinas.length;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <Emblema icono={Sparkles} />
        <Pastilla>{plantilla.pastilla}</Pastilla>
      </div>
      <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">{plantilla.tarjeta.titulo}</h2>
      <p className="mb-4 text-[13px] leading-relaxed text-texto-suave">
        {parcial
          ? `Te ${faltan.length === 1 ? 'falta 1' : `faltan ${faltan.length}`} de ${plantilla.rutinas.length}. Solo se agrega lo que falta; lo que ya tienes no cambia.`
          : plantilla.tarjeta.descripcion}
      </p>
      <BotonEnlace to="/gym/plantilla" icono={ArrowRight} variante="secundario">
        Revisar y crear
      </BotonEnlace>
    </Card>
  );
}

/** "Sigue": la sesión después de la última terminada, en las que no tienen días fijos. */
export function TarjetaSigue({
  siguiente,
  ocupado,
  onEmpezar,
}: {
  siguiente: SiguienteDelCiclo;
  ocupado: boolean;
  onEmpezar: (routineId: string) => void;
}) {
  const { ultima } = siguiente;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <span className="antetitulo">Sigue</span>
        <Pastilla>Días variables</Pastilla>
      </div>
      <h2 className="mb-1 mt-3 text-[20px] leading-tight tracking-[-0.5px] text-texto">{siguiente.nombre}</h2>
      <p className="mb-4 text-[13px] leading-relaxed text-texto-suave">
        {ultima === null
          ? 'Empieza por la primera. Al terminar cada sesión, Gym te propone la siguiente.'
          : `La última fue ${ultima.nombre}, el ${nombreDia(ultima.fecha)} ${formatearCorto(ultima.fecha)}. Puedes hacer esta u otra.`}
      </p>
      <Button icono={Play} tamano="grande" disabled={ocupado} onClick={() => onEmpezar(siguiente.routineId)}>
        Empezar
      </Button>
    </Card>
  );
}
